/**
 * Utility functions for vessel tracking data processing and interpolation.
 */

/** Sentinel value: "no vessels" display (Step 3 Vincent). null = all vessels, string id = one vessel. */
export const VESSEL_DISPLAY_NONE = '__none__';

/**
 * Bearing in degrees (0 = North, 90 = East) from point 1 to point 2.
 * @param {{ lat: number, lon: number }} p1 - Start point
 * @param {{ lat: number, lon: number }} p2 - End point
 * @returns {number} Bearing 0–360
 */
export const bearingBetweenPoints = (p1, p2) => {
    if (!p1 || !p2 || p1.lat == null || p1.lon == null || p2.lat == null || p2.lon == null) return 0;
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δλ = ((p2.lon - p1.lon) * Math.PI) / 180;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    let θ = (Math.atan2(y, x) * 180) / Math.PI;
    return (θ + 360) % 360;
};

/**
 * Check if a vessel has a travel path that actually moves over time (at least 2 points with distinct timestamps).
 * Used to show direction arrows only for vessels that will move when playback runs.
 * @param {Array} pointsOrTrack - Array of vessel points (e.g. rows from vesselsById.get(id), or one track from clusteredVesselData).
 * @returns {boolean} - True if the vessel has at least 2 points with valid lat/lon and distinct times.
 */
export const vesselHasTravelPath = (pointsOrTrack) => {
    if (!pointsOrTrack || !Array.isArray(pointsOrTrack)) return false;
    const validPoints = pointsOrTrack.filter(
        p => p && p.lat != null && p.lon != null && !isNaN(p.lat) && !isNaN(p.lon) && (p.timestamp || p.time)
    );
    if (validPoints.length < 2) return false;
    const times = new Set(validPoints.map(p => new Date(p.timestamp || p.time).getTime()));
    return times.size >= 2;
};

/**
 * Sorts vessel data by timestamp.
 * @param {Array} data - Raw vessel data.
 * @returns {Array} - Sorted data.
 */
export const sortVesselData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return [...data].sort((a, b) => {
        const tA = new Date(a.timestamp || a.time).getTime();
        const tB = new Date(b.timestamp || b.time).getTime();
        return tA - tB;
    });
};

/**
 * Groups vessel data into distinct tracks.
 * When unique_identifier is present, groups by it (one track per vessel) to avoid duplicate markers.
 * Otherwise falls back to distance/speed heuristics.
 */
export const clusterVesselData = (sortedData) => {
    if (!sortedData || sortedData.length === 0) return [];

    const hasIds = sortedData.some(p => p && (p.unique_identifier != null && p.unique_identifier !== ''));
    if (hasIds) {
        // One track per vessel: group by unique_identifier, sort each group by time
        const byId = new Map();
        for (const point of sortedData) {
            const id = point.unique_identifier != null && point.unique_identifier !== ''
                ? String(point.unique_identifier)
                : '__no_id__';
            if (!byId.has(id)) byId.set(id, []);
            byId.get(id).push(point);
        }
        const tracks = [];
        byId.forEach((points) => {
            const sorted = [...points].sort((a, b) => {
                const tA = new Date(a.timestamp || a.time).getTime();
                const tB = new Date(b.timestamp || b.time).getTime();
                return tA - tB;
            });
            tracks.push(sorted);
        });
        return tracks;
    }

    // Fallback: heuristic clustering when no unique_identifier
    const MAX_SPEED_KNOTS = 100;

    const getDistanceNM = (lat1, lon1, lat2, lon2) => {
        const R = 3440.065;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const tracks = [];
    for (const point of sortedData) {
        let addedToTrack = false;
        for (const track of tracks) {
            const lastPoint = track[track.length - 1];
            const t1 = new Date(lastPoint.timestamp || lastPoint.time).getTime();
            const t2 = new Date(point.timestamp || point.time).getTime();
            const hoursDiff = (t2 - t1) / (1000 * 60 * 60);
            if (hoursDiff === 0) continue;
            if (hoursDiff < 0) continue;
            const distNM = getDistanceNM(lastPoint.lat, lastPoint.lon, point.lat, point.lon);
            const impliedSpeed = distNM / hoursDiff;
            if (impliedSpeed <= MAX_SPEED_KNOTS) {
                track.push(point);
                addedToTrack = true;
                break;
            }
        }
        if (!addedToTrack) tracks.push([point]);
    }
    return tracks;
};

function rowIndicatesThreat(row) {
    if (!row) return false;
    if (row.threat_detected === 'Yes' || row.threat_detected === true) return true;
    if (Number(row.message_threat_level) > 0) return true;
    return false;
}

/** First timestamp where the track reports threat — used so playback shows green before, red after. */
function getFirstThreatTimeMs(sortedData) {
    if (!sortedData?.length) return null;
    for (const row of sortedData) {
        if (rowIndicatesThreat(row)) {
            const t = new Date(row.timestamp || row.time).getTime();
            if (!Number.isNaN(t)) return t;
        }
    }
    return null;
}

/**
 * Interpolates vessel state at a specific time for a single track.
 */
export const interpolateVesselState = (sortedData, currentTime) => {
    if (!sortedData || sortedData.length === 0) return null;

    const startTime = new Date(sortedData[0].timestamp || sortedData[0].time).getTime();
    const endTime = new Date(sortedData[sortedData.length - 1].timestamp || sortedData[sortedData.length - 1].time).getTime();
    const firstThreatMs = getFirstThreatTimeMs(sortedData);
    const threatYesAtPlayback =
        firstThreatMs != null && !Number.isNaN(firstThreatMs) && currentTime >= firstThreatMs;

    if (currentTime <= startTime) {
        const first = sortedData[0];
        if (sortedData.length >= 2) {
            return {
                ...first,
                movementBearing: bearingBetweenPoints(first, sortedData[1]),
                threat_detected: threatYesAtPlayback ? 'Yes' : 'No'
            };
        }
        return { ...first, threat_detected: threatYesAtPlayback ? 'Yes' : 'No' };
    }
    if (currentTime >= endTime) {
        const last = sortedData[sortedData.length - 1];
        if (sortedData.length >= 2) {
            return {
                ...last,
                movementBearing: bearingBetweenPoints(sortedData[sortedData.length - 2], last),
                threat_detected: threatYesAtPlayback ? 'Yes' : 'No'
            };
        }
        return { ...last, threat_detected: threatYesAtPlayback ? 'Yes' : 'No' };
    }

    let index = -1;
    for (let i = 0; i < sortedData.length - 1; i++) {
        const tCurrent = new Date(sortedData[i].timestamp || sortedData[i].time).getTime();
        const tNext = new Date(sortedData[i + 1].timestamp || sortedData[i + 1].time).getTime();
        if (currentTime >= tCurrent && currentTime < tNext) {
            index = i;
            break;
        }
    }

    if (index === -1) {
        const last = sortedData[sortedData.length - 1];
        return { ...last, threat_detected: threatYesAtPlayback ? 'Yes' : 'No' };
    }

    const p1 = sortedData[index];
    const p2 = sortedData[index + 1];

    const t1 = new Date(p1.timestamp || p1.time).getTime();
    const t2 = new Date(p2.timestamp || p2.time).getTime();

    const factor = (currentTime - t1) / (t2 - t1);

    const lerp = (start, end, t) => start + (end - start) * t;
    const lerpAngle = (start, end, t) => {
        const d = end - start;
        const delta = (d + 540) % 360 - 180;
        return (start + delta * t + 360) % 360;
    };

    const lat = lerp(p1.lat, p2.lat, factor);
    const lon = lerp(p1.lon, p2.lon, factor);
    const speed = lerp(p1.speed_knots || p1.speed || 0, p2.speed_knots || p2.speed || 0, factor);
    const heading = lerpAngle(p1.heading_deg || p1.heading || 0, p2.heading_deg || p2.heading || 0, factor);
    const reflectivity = lerp(p1.radar_reflectivity_db || 0, p2.radar_reflectivity_db || 0, factor);

    const movementBearing = bearingBetweenPoints(p1, p2);

    // Preserve metadata (common_name, identifiers) from track point so tooltips show exact category
    return {
        lat,
        lon,
        speed_knots: speed,
        heading_deg: heading,
        movementBearing,
        radar_reflectivity_db: reflectivity,
        threat_detected: threatYesAtPlayback ? 'Yes' : 'No',
        timestamp: currentTime,
        common_name: p1.common_name ?? p2.common_name ?? null,
        unique_identifier: p1.unique_identifier ?? p2.unique_identifier ?? null,
        message_description_label: p1.message_description_label ?? p2.message_description_label ?? null,
        message_description_name: p1.message_description_name ?? p2.message_description_name ?? null,
        object_classification: p1.object_classification ?? p2.object_classification ?? null
    };
};

/**
 * Interpolates vessel states for multiple separate tracks.
 */
export const interpolateMultipleVesselStates = (clusteredTracks, currentTime) => {
    if (!clusteredTracks || clusteredTracks.length === 0) return [];

    const states = [];
    clusteredTracks.forEach((track, idx) => {
        const trackStart = new Date(track[0].timestamp || track[0].time).getTime();
        const trackEnd = new Date(track[track.length - 1].timestamp || track[track.length - 1].time).getTime();

        // Hide vessel if not currently in its recorded timeline
        if (currentTime < trackStart || currentTime > trackEnd) return;

        const state = interpolateVesselState(track, currentTime);
        if (state) {
            states.push({ ...state, trackId: idx });
        }
    });

    return states;
};
